import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  Star, 
  Quote, 
  ArrowRight, 
  Briefcase,
  GraduationCap,
  Award,
  ChevronLeft
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const testimonials = [
  {
    id: 1,
    name: "Marcus Johnson",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    branch: "U.S. Army",
    rank: "Staff Sergeant (E-6)",
    mos: "11B - Infantry",
    currentRole: "Security Operations Manager",
    company: "Amazon Web Services",
    quote: "Atlas helped me translate 12 years of infantry leadership into corporate language. Within 3 months of using the platform, I landed a six-figure role at AWS. The MOS translator and AI career coach were game-changers.",
    achievement: "Secured $125K+ role",
    timeframe: "3 months"
  },
  {
    id: 2,
    name: "Sarah Mitchell",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face",
    branch: "U.S. Navy",
    rank: "Chief Petty Officer (E-7)",
    mos: "HM - Hospital Corpsman",
    currentRole: "Healthcare Program Director",
    company: "Veterans Affairs Medical Center",
    quote: "The document generator created a resume that finally captured my 15 years of Navy medical experience properly. The ATS scorer helped me understand why I wasn't getting callbacks before. Now I'm leading a team of 40.",
    achievement: "Promoted to Director",
    timeframe: "4 months"
  },
  {
    id: 3,
    name: "David Chen",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    branch: "U.S. Air Force",
    rank: "Technical Sergeant (E-6)",
    mos: "1A8X1 - Airborne Cryptologic Language Analyst",
    currentRole: "Cybersecurity Analyst",
    company: "Lockheed Martin",
    quote: "I was struggling to explain my classified work experience to civilian employers. Atlas's AI chatbot helped me craft stories that highlighted my skills without violating my clearance. The learning modules on interview prep were exactly what I needed.",
    achievement: "Cleared contractor role",
    timeframe: "6 weeks"
  },
  {
    id: 4,
    name: "Jessica Rodriguez",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    branch: "U.S. Marine Corps",
    rank: "Gunnery Sergeant (E-7)",
    mos: "0311 - Rifleman",
    currentRole: "Operations Manager",
    company: "FedEx Logistics",
    quote: "Coming from combat arms, I thought my only options were security or law enforcement. Atlas opened my eyes to operations and logistics roles. The job matching AI found opportunities I never would have considered.",
    achievement: "Career pivot to logistics",
    timeframe: "2 months"
  },
  {
    id: 5,
    name: "Michael Thompson",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
    branch: "U.S. Army",
    rank: "Captain (O-3)",
    mos: "25A - Signal Officer",
    currentRole: "IT Project Manager",
    company: "Microsoft",
    quote: "The transition from military to tech was smoother than I expected thanks to Atlas. The platform helped me understand how my signal corps experience translated to project management. I completed my PMP while using the learning suite.",
    achievement: "Landed Big Tech role",
    timeframe: "5 months"
  },
  {
    id: 6,
    name: "Amanda Foster",
    image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face",
    branch: "U.S. Coast Guard",
    rank: "Petty Officer First Class (E-6)",
    mos: "ME - Maritime Enforcement Specialist",
    currentRole: "Compliance Officer",
    company: "Bank of America",
    quote: "I never thought my Coast Guard law enforcement background would translate to banking, but Atlas showed me the connection. The benefits chatbot also helped me understand my VA healthcare options during my transition.",
    achievement: "Financial sector career",
    timeframe: "3 months"
  }
];

const Testimonials = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white dark:from-navy-900 dark:to-navy-800">
      {/* Header */}
      <header className="bg-white dark:bg-navy-800 border-b border-navy-100 dark:border-navy-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="w-8 h-8 text-amber-600" />
              <span className="font-heading text-xl font-bold text-navy-900 dark:text-white">
                Atlas Career Solutions
              </span>
            </Link>
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="dark:text-white">Sign In</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-amber-600 hover:bg-amber-700">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-navy-600 dark:text-navy-400 hover:text-amber-600 mb-8">
          <ChevronLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Hero */}
        <div className="text-center mb-16">
          <Badge className="bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 mb-4">
            Success Stories
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-navy-900 dark:text-white mb-4">
            Veterans Who Transformed Their Careers
          </h1>
          <p className="text-xl text-navy-600 dark:text-navy-300 max-w-3xl mx-auto">
            Real stories from service members who used Atlas Career Solutions to transition into 
            successful civilian careers.
          </p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-6 mb-16">
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 text-center">
            <CardContent className="p-6">
              <div className="text-4xl font-bold text-amber-600 mb-2">2,500+</div>
              <p className="text-navy-600 dark:text-navy-300">Veterans Placed</p>
            </CardContent>
          </Card>
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 text-center">
            <CardContent className="p-6">
              <div className="text-4xl font-bold text-amber-600 mb-2">$95K</div>
              <p className="text-navy-600 dark:text-navy-300">Average Starting Salary</p>
            </CardContent>
          </Card>
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 text-center">
            <CardContent className="p-6">
              <div className="text-4xl font-bold text-amber-600 mb-2">94%</div>
              <p className="text-navy-600 dark:text-navy-300">Satisfaction Rate</p>
            </CardContent>
          </Card>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {testimonials.map((testimonial) => (
            <Card 
              key={testimonial.id} 
              className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 overflow-hidden"
            >
              <CardContent className="p-0">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-navy-900 to-navy-800 p-6">
                  <div className="flex items-start gap-4">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-amber-500"
                    />
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-semibold text-white">
                        {testimonial.name}
                      </h3>
                      <p className="text-navy-300 text-sm">{testimonial.branch}</p>
                      <p className="text-navy-400 text-sm">{testimonial.rank}</p>
                    </div>
                    <Badge className="bg-emerald-500 text-white">
                      {testimonial.achievement}
                    </Badge>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6">
                  {/* Quote */}
                  <div className="relative mb-6">
                    <Quote className="absolute -top-2 -left-2 w-8 h-8 text-amber-200 dark:text-amber-900" />
                    <p className="text-navy-700 dark:text-navy-200 italic pl-6">
                      "{testimonial.quote}"
                    </p>
                  </div>

                  {/* Career Transition */}
                  <div className="bg-navy-50 dark:bg-navy-700/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2 text-sm text-navy-500 dark:text-navy-400 mb-2">
                      <Briefcase className="w-4 h-4" />
                      Career Transition
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-navy-500 dark:text-navy-400">FROM</p>
                        <p className="font-medium text-navy-900 dark:text-white text-sm">
                          {testimonial.mos}
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-amber-600" />
                      <div className="text-right">
                        <p className="text-xs text-navy-500 dark:text-navy-400">TO</p>
                        <p className="font-medium text-navy-900 dark:text-white text-sm">
                          {testimonial.currentRole}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-amber-600 mt-2">@ {testimonial.company}</p>
                  </div>

                  {/* Timeframe */}
                  <div className="flex items-center gap-2 text-sm text-navy-500 dark:text-navy-400">
                    <Award className="w-4 h-4 text-amber-600" />
                    <span>Achieved in {testimonial.timeframe}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA Section */}
        <Card className="border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-500 to-amber-600 overflow-hidden">
          <CardContent className="p-8 text-center text-white">
            <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-amber-100 max-w-2xl mx-auto mb-6">
              Join thousands of veterans who have transformed their military experience into 
              thriving civilian careers with Atlas Career Solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isAuthenticated ? (
                <Link to="/membership">
                  <Button size="lg" className="bg-white text-amber-600 hover:bg-amber-50">
                    Activate Membership
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/register">
                    <Button size="lg" className="bg-white text-amber-600 hover:bg-amber-50">
                      Start Your Journey
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                      I Have an Account
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-navy-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="w-6 h-6 text-amber-500" />
            <span className="font-heading font-bold">Atlas Career Solutions</span>
          </div>
          <p className="text-navy-400 text-sm">
            Empowering veterans to achieve civilian career success.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Testimonials;
